// GroupSection.tsx
import type React from 'react'
import { useState } from 'react'
import type { IProjectSample, ProjectOrder } from '../../../Model/Project/IProjectSample.ts'
import { ProjectParticipantBadge } from './ProjectParticipantBadge.tsx'
import { Link } from 'react-router-dom'
import { getIconFromTaxa } from 'Util/iconUtil.tsx'
import { selectParticipantsByProjectId } from '../../../Flux/Selector/index.js'
import type { IResolvedParticipant } from '../../../Model/Project/IResolvedParticipant.js'
import { useSelector } from 'react-redux'
import type { Sample } from '../../../Model/index.js'
import { DateWidget } from '../../Common/DateWidget.js'

type GroupSectionProps = {
  label: string
  samples: IProjectSample[]
  orders: Record<string, ProjectOrder>
  editing: boolean
  projectId: string
  selectedSamples: number[]
  onSelectionChange: (selectedIds: number[]) => void
}

export const GroupSection: React.FC<GroupSectionProps> = ({
  label,
  samples,
  orders,
  editing,
  projectId,
  selectedSamples,
  onSelectionChange
}) => {
  const [collapsed, setCollapsed] = useState(false)

  const participants = useSelector(selectParticipantsByProjectId(projectId))

  const orderForItem = (sample: Sample) => (sample.orderId ? orders[sample.orderId] : undefined)

  const mapParticipants = (input: 'LOADING' | IResolvedParticipant[]) => {
    if (input === 'LOADING') {
      return {}
    } else {
      return input.reduce(
        (acc, current) => {
          acc[current.user.id] = current
          return acc
        },
        {} as Record<string, IResolvedParticipant>
      )
    }
  }

  const participantsById = mapParticipants(participants)

  const handleCheckboxChange = (sampleId: number, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedSamples, sampleId])
    } else {
      onSelectionChange(selectedSamples.filter((id) => id !== sampleId))
    }
  }

  const handleGroupCheckboxChange = (checked: boolean) => {
    if (checked) {
      // Add all samples in this group that aren't already selected
      const sampleIds = samples.map((sample) => sample.id)
      const newSelectedSamples = [...selectedSamples]
      sampleIds.forEach((id) => {
        if (!selectedSamples.includes(id)) {
          newSelectedSamples.push(id)
        }
      })
      onSelectionChange(newSelectedSamples)
    } else {
      // Remove all samples in this group
      const sampleIds = samples.map((sample) => sample.id)
      onSelectionChange(selectedSamples.filter((id) => !sampleIds.includes(id)))
    }
  }

  return (
    <>
      <tr className="bg-base-200 hover:bg-base-300">
        <td colSpan={editing ? 1 : 0} className="px-1">
          {editing && (
            <input
              data-testid={'sample-checkbox'}
              type="checkbox"
              className="checkbox"
              checked={samples.length > 0 && samples.every((sample) => selectedSamples.includes(sample.id))}
              onChange={(e) => handleGroupCheckboxChange(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </td>
        <td colSpan={5} className="font-semibold cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
          <span className="py-2 px-2 flex justify-between items-center">
            <span>{label}</span>
            <span className="text-sm text-gray-600">{collapsed ? 'Show' : 'Hide'}</span>
          </span>
        </td>
      </tr>
      {!collapsed &&
        samples.map((item) => (
          <tr data-testid="project-sample-row" key={item.id}>
            {editing && (
              <td className="px-1">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={selectedSamples.includes(item.id)}
                  onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
                />
              </td>
            )}
            <td>{getIconFromTaxa(orderForItem(item.sample)?.anonymousOrder?.targetTaxonomy ?? null, 'text-xl')}</td>
            <td>{item.sample.customerSampleName || 'N/A'}</td>
            <td>
              {item.sample.orderId ? (
                orderForItem(item.sample)?.fullOrder?.name ? (
                  <Link to={`/order/${item.sample.orderId}`} className="text-blue-600 hover:underline">
                    {orderForItem(item.sample)?.fullOrder?.name || item.sample.orderId}
                  </Link>
                ) : (
                  <span>{item.sample.orderId}</span>
                )
              ) : (
                '–'
              )}
            </td>

            <td>{item.sample.otuCount}</td>
            <td>
              {participantsById[item.addedByUser] ? (
                <ProjectParticipantBadge user={participantsById[item.addedByUser].user} mayEdit={false} />
              ) : (
                item.addedByUser
              )}
            </td>
            <td>
              <DateWidget date={item.addedAt} />
            </td>
          </tr>
        ))}
    </>
  )
}
